import { Fragment, type ReactNode } from 'react'
import { formatRevenueCompact, formatRevenueFull } from './formatters'
import YtdLine from './YtdLine'
import { DiffValue, publishedValue, type RevisionBaseline } from './DiffBits'
import RowCommentButton from './RowCommentButton'
import QuarterlySummaryRow from './QuarterlySummaryRow'
import QuarterProgress from './QuarterProgress'
import { type Channel, type Quarter, type QuarterlyMatrix, growthLabel, pastYear, sumOver, withYear } from './quarterlyTypes'
import { type RowAnchor } from './rowAnchor'
import { type CellAnchor, quarterLabel } from './cellAnchor'
import CommentableCell, { rowClickProps } from './CommentableCell'

interface Props extends Omit<QuarterlyMatrix, 'actual_quarters' | 'ytd' | 'actual'> {
  actual_quarters?: Quarter[]
  ytd?: Record<string, Record<string, string>>
  actual?: Record<string, Record<string, string>>
  currency?: string | null
  // A revision under review: a cell that differs from the published plan
  // shows the new value on a green line over the published value on a red
  // line (GitHub diff styling).
  baseline?: RevisionBaseline | null
  onRowAction?: (anchor: RowAnchor) => void
  rowExtras?: (anchor: RowAnchor) => ReactNode
  onCellAction?: (cell: CellAnchor) => void
  cellCommentCount?: (cell: CellAnchor) => number
  cellSelected?: (cell: CellAnchor) => boolean
}

// Column widths in px; the table scales them together to fill its width.
const COL = { channel: 120, past: 76, pastTotal: 90, actual: 116, planHalf: 90, growth: 90, target: 110, fullYear: 140 }

export default function VersionViewQuarterlySection({
  channels,
  past_quarters,
  actual_quarters = [],
  plan_quarters,
  past,
  plan,
  ytd = {},
  actual = {},
  actual_targets = {},
  channel_targets,
  currency,
  baseline = null,
  onRowAction,
  rowExtras,
  onCellAction,
  cellCommentCount,
  cellSelected,
}: Props) {
  const wasCell = (channelId: string, quarterStart: string): number | undefined =>
    publishedValue(baseline, (b) => b.quarter_targets[channelId]?.[quarterStart])
  const wasTarget = (channelId: string): number | undefined =>
    publishedValue(baseline, (b) => b.channel_totals[channelId])
  // A revision adds a Full year column after Target: sold months + the
  // remaining target, green over the published year under review.
  const fullYearColumn = actual_quarters.length > 0
  // Past quarters get a Total column after them (e.g. "Total 2025").
  const pastTotalColumn = past_quarters.length > 0
  const versions = baseline?.version_numbers
  const plainColumns = past_quarters.length + (pastTotalColumn ? 1 : 0) + actual_quarters.length + 2 * plan_quarters.length
  const plainWidth = COL.past * past_quarters.length + (pastTotalColumn ? COL.pastTotal : 0)
    + COL.actual * actual_quarters.length + 2 * COL.planHalf * plan_quarters.length
  // Channel + Growth + Target, plus Full year on a revision.
  const colCount = 3 + plainColumns + (fullYearColumn ? 1 : 0)
  // The year group starts with a heavy rule: at Growth on a plain version
  // (Growth | Target), at Target on a revision (Target | Growth | Full year).
  const growthBorderLeft = fullYearColumn ? '1px solid var(--border)' : '2px solid var(--border)'
  const targetBorderLeft = fullYearColumn ? '2px solid var(--border)' : '1px solid var(--border)'
  const matrix: QuarterlyMatrix = { channels, past_quarters, actual_quarters, plan_quarters, past, plan, ytd, actual, actual_targets, channel_targets }
  const growthHeader = (
    <th className="text-right px-3 py-2 border-b" style={{ width: 90, borderColor: 'var(--border)', borderLeft: growthBorderLeft }}>
      Growth
    </th>
  )
  return (
    <div className="border rounded-md overflow-x-auto" style={{ borderColor: 'var(--border)', background: 'var(--background)' }}>
      {/* Fixed layout, every column sized by the colgroup and scaled together
          to fill the width. Past quarters are narrow (compact numbers),
          actual quarters wider; each Plan quarter is two columns (its cells
          span two, full numbers need the room). */}
      <table
        className="w-full text-sm border-collapse"
        style={{ tableLayout: 'fixed', minWidth: COL.channel + plainWidth + COL.growth + COL.target + (fullYearColumn ? COL.fullYear : 0) }}
      >
        <colgroup>
          <col style={{ width: COL.channel }} />
          {past_quarters.map((q) => <col key={`past-${q.start_date}`} style={{ width: COL.past }} />)}
          {pastTotalColumn && <col style={{ width: COL.pastTotal }} />}
          {actual_quarters.map((q) => <col key={`actual-${q.start_date}`} style={{ width: COL.actual }} />)}
          {plan_quarters.flatMap((q) => [0, 1].map((i) => <col key={`plan-${q.start_date}-${i}`} style={{ width: COL.planHalf }} />))}
          {!fullYearColumn && <col style={{ width: COL.growth }} />}
          <col style={{ width: COL.target }} />
          {fullYearColumn && <col style={{ width: COL.growth }} />}
          {fullYearColumn && <col style={{ width: COL.fullYear }} />}
        </colgroup>
        <thead>
          <tr className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
            <th className="text-left px-3 py-2 border-b sticky left-0 bg-background" style={{ width: 120, minWidth: 120, borderColor: 'var(--border)' }}>
              Channel
            </th>
            {past_quarters.map((q, i) => (
              <th
                key={`past-${q.start_date}`}
                className="text-center px-3 py-2 border-b"
                style={{ borderColor: 'var(--border)', borderLeft: i === 0 ? '1px solid var(--border)' : undefined }}
              >
                {q.label}
              </th>
            ))}
            {pastTotalColumn && (
              <th className="text-center px-3 py-2 border-b" style={{ borderColor: 'var(--border)', borderLeft: '1px solid var(--border)' }}>
                Total {pastYear(past_quarters)}
              </th>
            )}
            {actual_quarters.map((q, i) => (
              <th
                key={`actual-${q.start_date}`}
                className="text-center px-3 py-2 border-b"
                style={{ borderColor: 'var(--border)', borderLeft: i === 0 ? '2px solid var(--border)' : undefined }}
              >
                Actual {withYear(q)}
              </th>
            ))}
            {plan_quarters.map((q, i) => (
              <th
                key={`plan-${q.start_date}`}
                colSpan={2}
                className="text-right px-3 py-2 border-b"
                style={{ borderColor: 'var(--border)', borderLeft: i === 0 ? '2px solid var(--border)' : undefined }}
              >
                Plan {withYear(q)}
              </th>
            ))}
            {!fullYearColumn && growthHeader}
            <th
              className="text-right px-3 py-2 border-b"
              style={{ width: 110, borderColor: 'var(--border)', borderLeft: targetBorderLeft }}
            >
              Target
            </th>
            {fullYearColumn && growthHeader}
            {fullYearColumn && (
              <th className="text-right px-3 py-2 border-b" style={{ width: 140, borderColor: 'var(--border)' }}>
                <div>Full year</div>
                {versions && (
                  <div className="text-[10px] font-normal" style={{ color: 'var(--muted-foreground)' }}>
                    v{versions.now} vs v{versions.was}
                  </div>
                )}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {channels.length === 0 ? (
            <tr>
              <td
                colSpan={colCount}
                className="px-3 py-6 text-center"
                style={{ color: 'var(--muted-foreground)' }}
              >
                No channels.
              </td>
            </tr>
          ) : (
            channels.map((c: Channel) => {
              const target = channel_targets[c.id] ?? 0
              const channelPastTotal = sumOver(past_quarters, past[c.id])
              const channelYtdTotal = sumOver(plan_quarters, ytd[c.id])
              const channelActualTotal = sumOver(actual_quarters, actual[c.id])
              const anchor: RowAnchor = { section: 'quarterly', anchor_type: 'OrgNode', anchor_id: c.id, label: c.name }
              const extra = rowExtras?.(anchor)
              // The year this row plans: sold months + remaining target on a
              // revision, the target itself on a plain version.
              const fullYear = fullYearColumn ? (baseline?.channel_totals_full_year?.[c.id] ?? (channelActualTotal + target)) : target
              const growthCell = (
                <td
                  className="px-3 py-2 border-b text-right tabular-nums"
                  style={{ borderColor: 'var(--border)', borderLeft: growthBorderLeft, color: 'var(--foreground)' }}
                >
                  <DiffValue was={wasTarget(c.id)} now={fullYear} format={(x) => growthLabel(channelPastTotal, x)} />
                </td>
              )
              // With one planned quarter the row has one commentable number, so
              // the whole row opens its comments (like Overall Plan).
              const onlyQuarter = plan_quarters.length === 1 ? plan_quarters[0] : null
              const rowAction = onCellAction && onlyQuarter
                ? () => onCellAction({ ...anchor, column_key: onlyQuarter.start_date, column_label: quarterLabel(onlyQuarter.start_date) })
                : undefined
              return (
                <Fragment key={c.id}>
                  <tr
                    className={`${onRowAction ? 'group' : ''} ${rowAction ? 'cursor-pointer hover:bg-primary/5' : ''}`}
                    {...rowClickProps(`Comments on ${c.name}`, rowAction)}
                  >
                    <td
                      className="relative px-3 py-2 border-b sticky left-0 bg-background"
                      style={{ borderColor: 'var(--border)', paddingLeft: onRowAction ? 32 : 12, color: 'var(--foreground)' }}
                    >
                      {onRowAction && <RowCommentButton label={c.name} onClick={() => onRowAction(anchor)} />}
                      {c.name}
                    </td>
                    {past_quarters.map((q, i) => {
                      const v = Number(past[c.id]?.[q.start_date] ?? 0)
                      return (
                        <td
                          key={`past-${c.id}-${q.start_date}`}
                          className="px-3 py-2 border-b text-center tabular-nums"
                          style={{ borderColor: 'var(--border)', color: 'var(--muted-foreground)', borderLeft: i === 0 ? '1px solid var(--border)' : undefined }}
                        >
                          {v > 0 ? formatRevenueCompact(v, currency) : '—'}
                        </td>
                      )
                    })}
                    {pastTotalColumn && (
                      <td
                        className="px-3 py-2 border-b text-center tabular-nums"
                        style={{ borderColor: 'var(--border)', color: 'var(--muted-foreground)', borderLeft: '1px solid var(--border)' }}
                      >
                        {channelPastTotal > 0 ? formatRevenueCompact(channelPastTotal, currency) : '—'}
                      </td>
                    )}
                    {actual_quarters.map((q, i) => {
                      const v = Number(actual[c.id]?.[q.start_date] ?? 0)
                      return (
                        <td
                          key={`actual-${c.id}-${q.start_date}`}
                          className="px-3 py-2 border-b text-center tabular-nums"
                          style={{ borderColor: 'var(--border)', color: 'var(--foreground)', borderLeft: i === 0 ? '2px solid var(--border)' : undefined }}
                        >
                          {v > 0 ? formatRevenueCompact(v, currency) : '—'}
                          <QuarterProgress actual={v} target={actual_targets[c.id]?.[q.start_date]} currency={currency} />
                        </td>
                      )
                    })}
                    {plan_quarters.map((q, i) => {
                      const cell: CellAnchor = { ...anchor, column_key: q.start_date, column_label: quarterLabel(q.start_date) }
                      const v = plan[c.id]?.[q.start_date] ?? 0
                      const qYtd = Number(ytd[c.id]?.[q.start_date] ?? 0)
                      const was = wasCell(c.id, q.start_date)
                      return (
                        <CommentableCell
                          key={`plan-${c.id}-${q.start_date}`}
                          cell={cell}
                          onCellAction={rowAction ? undefined : onCellAction}
                          selected={cellSelected?.(cell) ?? false}
                          colSpan={2}
                          className="px-3 py-2 border-b text-right tabular-nums"
                          style={{ borderColor: 'var(--border)', borderLeft: i === 0 ? '2px solid var(--border)' : undefined, color: v ? 'var(--foreground)' : 'var(--muted-foreground)' }}
                        >
                          <DiffValue was={was} now={v} format={(x) => (x ? formatRevenueFull(x, currency) : '—')} mark={{ count: cellCommentCount?.(cell) ?? 0 }} />
                          <YtdLine ytd={qYtd} target={v} currency={currency} />
                        </CommentableCell>
                      )
                    })}
                    {!fullYearColumn && growthCell}
                    <td
                      className="px-3 py-2 border-b text-right tabular-nums"
                      style={{ borderColor: 'var(--border)', borderLeft: targetBorderLeft, color: 'var(--foreground)' }}
                    >
                      {fullYearColumn
                        ? (target > 0 ? formatRevenueCompact(target, currency) : '—')
                        : <DiffValue was={wasTarget(c.id)} now={target} format={(x) => (x > 0 ? formatRevenueCompact(x, currency) : '—')} />}
                      <YtdLine ytd={channelYtdTotal} target={target} currency={currency} />
                    </td>
                    {fullYearColumn && growthCell}
                    {fullYearColumn && (
                      <td className="px-3 py-2 border-b text-right tabular-nums" style={{ borderColor: 'var(--border)', color: 'var(--foreground)' }}>
                        <DiffValue was={wasTarget(c.id)} now={fullYear} format={(x) => formatRevenueCompact(x, currency)} />
                      </td>
                    )}
                  </tr>
                  {extra && (
                    <tr>
                      <td colSpan={colCount} className="p-0 border-b" style={{ borderColor: 'var(--border)' }}>{extra}</td>
                    </tr>
                  )}
                </Fragment>
              )
            })
          )}
          {channels.length > 0 && (
            <QuarterlySummaryRow {...matrix} currency={currency} baseline={baseline} fullYearColumn={fullYearColumn} />
          )}
        </tbody>
      </table>
    </div>
  )
}
